import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Typography, Tag, Select, Space, Button, Modal, Input, Textarea, InputNumber, Popconfirm, DatePicker, Descriptions, Tooltip, Spinner, EmptyState, Tabs, Table, Drawer, message } from '../../ui';
import type { TagTone } from '../../ui';
import { Plus, Eye, Pencil, Trash2, Search, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { TwDataTable as DataTable } from '../../components/TwDataTable';
import type { DataTableColumn } from '../../components/TwDataTable';
import { usePermission, useAuthStore } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useBadges } from '../../hooks/useBadges';
import { formatIst, formatIstDate } from '../../lib/time';
import { FileUploadField } from '../../components/FileUploadField';
import { AdvanceRequestsPage, RequestDetailPanel } from './AdvanceRequestsPage';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Expense-report status chip colours (mirrors the Expense Reports page).
const REPORT_STATUS_COLOR: Record<string, TagTone> = {
  DRAFT: 'neutral', SUBMITTED: 'gold', APPROVED: 'cyan', CLOSED: 'green', CORRECTION_REQUIRED: 'gold', REJECTED: 'red',
};

// Advance / top-up request status chip colours.
const ADV_STATUS_COLOR: Record<string, TagTone> = {
  REQUESTED: 'gold', APPROVED: 'blue', DISBURSED: 'green', REJECTED: 'red',
};

const ROLE_TAG: Record<string, { label: string; tone: TagTone }> = {
  SUPER_ADMIN: { label: 'Super Admin', tone: 'red' },
  ADMIN: { label: 'Admin', tone: 'red' },
  ACCOUNTS: { label: 'Accounts', tone: 'gold' },
  SALES_HEAD: { label: 'Sales Head', tone: 'purple' },
  SALES_PERSON: { label: 'Sales Person', tone: 'blue' },
};
const RoleTag = ({ role }: { role?: string }) =>
  role && ROLE_TAG[role] ? <Tag tone={ROLE_TAG[role].tone}>{ROLE_TAG[role].label}</Tag> : null;

// Wallet balance semantics: a positive balance is company float still sitting with the
// rep (outstanding), a negative balance means the rep is owed a reimbursement, zero is settled.
function balanceColor(v: any): string {
  const n = Number(v ?? 0);
  if (n > 0) return '#1F8A4C';
  if (n < 0) return '#D9363E';
  return '#8c8c8c';
}
function balanceLabel(v: any): string {
  const n = Number(v ?? 0);
  if (n > 0) return 'float outstanding';
  if (n < 0) return 'reimbursement due';
  return 'settled';
}
// Balance sign → Tag tone (green = float outstanding, red = reimbursement due, neutral = settled).
function balanceTone(v: any): TagTone {
  const c = balanceColor(v);
  return c === '#1F8A4C' ? 'green' : c === '#D9363E' ? 'red' : 'neutral';
}

const TXN_TYPES = ['ADVANCE_IN', 'REIMBURSEMENT', 'RETURN', 'ADJUSTMENT', 'OPENING_BALANCE'] as const;
const TXN_TYPE_COLOR: Record<string, TagTone> = {
  ADVANCE_IN: 'blue', REIMBURSEMENT: 'cyan', RETURN: 'gold', ADJUSTMENT: 'purple', OPENING_BALANCE: 'blue',
};
const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'];
const VERIFY_COLOR: Record<string, TagTone> = {
  NOT_REQUIRED: 'neutral', UNVERIFIED: 'gold', VERIFIED: 'green',
};

export function WalletsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canApproveHead = usePermission('EXPENSE.APPROVE.HEAD');
  const canApproveAcct = usePermission('EXPENSE.APPROVE.ACCT');
  const isFleet =
    canApproveHead || canApproveAcct ||
    role === 'SALES_HEAD' || role === 'ADMIN' || role === 'ACCOUNTS' || role === 'SUPER_ADMIN';

  return isFleet ? <FleetView canRecord={canApproveAcct} /> : <MyWalletView />;
}

// Straight-line (haversine) distance in km — the always-available fallback for road
// distance/ETA (an OSRM /table call could replace this when an OSRM endpoint is configured).
function km(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371, toR = (d: number) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// ── Fleet view: managers/admins see every rep's wallet ───────────────────────
function FleetView({ canRecord }: { canRecord: boolean }) {
  const badges = useBadges();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [balance, setBalance] = useState<string | undefined>();
  const [drawerRepId, setDrawerRepId] = useState<string | null>(null);
  const [adminPos, setAdminPos] = useState<{ lat: number; lng: number } | null>(null);
  const [giveFor, setGiveFor] = useState<any | null>(null);
  const askMyLocation = () => {
    if (!navigator.geolocation) { message.warning('No geolocation in this browser'); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setAdminPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => message.warning('Could not get your location'),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
    );
  };

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (search.trim()) p.search = search.trim();
    if (balance) p.balance = balance;
    return p;
  }, [search, balance]);

  const filtersActive = !!(search.trim() || balance);
  const clearFilters = () => { setSearchInput(''); setBalance(undefined); };

  const { data: stats } = useQuery({
    queryKey: ['wallet-stats'],
    queryFn: () => request<any>({ url: '/wallets/stats' }),
  });
  const { data, isLoading } = useQuery({
    queryKey: ['wallets', params],
    queryFn: () => request<any[]>({ url: '/wallets', params }),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<any>[] = [
    {
      key: 'repName', title: 'Sales Person', dataIndex: 'repName', sortable: true, alwaysVisible: true, width: 200,
      render: (v: any, r: any) => (
        <div>
          <Space size={6} wrap>
            <b>{v ?? `#${r.repId}`}</b>
            <RoleTag role={r.repRole} />
          </Space>
          {r.repPhone && <div style={{ color: '#888', fontSize: 12 }}>{r.repPhone}</div>}
        </div>
      ),
    },
    {
      key: 'currentBalance', title: 'Current balance', dataIndex: 'currentBalance', sortable: true, align: 'right', alwaysVisible: true,
      render: (v: any) => (
        <Tooltip label={balanceLabel(v)}>
          <b style={{ color: balanceColor(v) }}>{inr(v)}</b>
        </Tooltip>
      ),
    },
    { key: 'lastActivityAt', title: 'Last activity', dataIndex: 'lastActivityAt', sortable: true, render: (v: string) => v ? formatIst(v) : '—' },
    { key: 'toursMonth', title: 'Tours (month)', dataIndex: 'toursMonth', align: 'right', render: (v: any) => v ?? 0 },
    { key: 'completedMonth', title: 'Completed (month)', dataIndex: 'completedMonth', align: 'right', defaultVisible: false, render: (v: any) => v ?? 0 },
    { key: 'pendingTours', title: 'Pending tours', dataIndex: 'pendingTours', align: 'right', render: (v: any) => v ? <Tag tone="gold">{v}</Tag> : 0 },
    { key: 'tourAdvance', title: 'Tour advance', dataIndex: 'tourAdvance', align: 'right', render: (v: any) => Number(v) > 0 ? <b style={{ color: '#1F8A4C' }}>{inr(v)}</b> : <span style={{ color: '#ccc' }}>—</span> },
    { key: 'returnable', title: 'Returnable', dataIndex: 'returnable', align: 'right', render: (v: any) => Number(v) > 0 ? inr(v) : <span style={{ color: '#ccc' }}>—</span> },
    { key: 'reimbursementDue', title: 'Reimbursement due', dataIndex: 'reimbursementDue', align: 'right', defaultVisible: false, render: (v: any) => Number(v) > 0 ? <b style={{ color: '#D9363E' }}>{inr(v)}</b> : <span style={{ color: '#ccc' }}>—</span> },
    { key: 'approvalSubmission', title: 'Approval submission', dataIndex: 'approvalSubmission', align: 'right', render: (v: any) => v ? <Tag tone="blue">{v}</Tag> : 0 },
    {
      key: 'lastLocation', title: 'Last location', dataIndex: 'lastLocation',
      render: (loc: any) => loc
        ? (
          <Space size={4} wrap>
            <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} aria-label="Open location in Google Maps" title="Open in Google Maps" className="inline-flex items-center text-brand-700 hover:text-brand-800 dark:text-brand-400"><MapPin size={14} /></a>
            {adminPos && <Tag tone="blue">~{km(adminPos, loc).toFixed(1)} km</Tag>}
            <span className="text-xs text-muted">{formatIstDate(loc.at)}</span>
          </Space>
        )
        : <span style={{ color: '#ccc' }}>no GPS</span>,
    },
    {
      key: 'actions', title: 'Action', dataIndex: 'id', alwaysVisible: true, width: 130,
      render: (_: any, r: any) => (
        <Space size={6} onClick={(e) => e.stopPropagation()}>
          <Tooltip label="Open wallet"><Button variant="text" aria-label="Open wallet" icon={<Eye size={16} />} onClick={() => setDrawerRepId(String(r.repId))} /></Tooltip>
          {canRecord && <Tooltip label="Give advance"><Button size="sm" variant="primary" aria-label="Give advance" icon={<Plus size={16} />} onClick={() => setGiveFor(r)} /></Tooltip>}
        </Space>
      ),
    },
  ];

  const advancesTab = (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <KpiTile label="Sales persons with float" value={String(stats?.reps_with_float ?? 0)} color="#1677FF" bg="#E6F4FF" muted={!stats?.reps_with_float} />
        <KpiTile label="Reimbursement due (sales persons)" value={String(stats?.reimbursement_due ?? 0)} color="#D9363E" bg="#FFF1F0" muted={!stats?.reimbursement_due} />
        <KpiTile label="Total float outstanding" value={inr(stats?.total_float_outstanding)} color="#1F8A4C" bg="#F6FFED" muted={!Number(stats?.total_float_outstanding)} />
        <KpiTile label="Pending verification" value={String(stats?.pending_verification ?? 0)} color="#D48806" bg="#FFFBE6" muted={!stats?.pending_verification} />
      </div>

      <Card>
        <DataTable
          storageKey="sp-advances"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          onRow={(r: any) => ({ onClick: () => setDrawerRepId(String(r.repId)), style: { cursor: 'pointer' } })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          emptyText={<EmptyState title="No sales persons" />}
          toolbarLeft={
            <Space wrap>
              <Input prefix={<Search size={15} />} placeholder="Sales person name or phone" className="w-[220px]"
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              <Select placeholder="Balance" className="w-[190px]" value={balance} onChange={setBalance}
                options={[
                  { value: 'POSITIVE', label: 'Float outstanding' },
                  { value: 'NEGATIVE', label: 'Reimbursement due' },
                  { value: 'ZERO', label: 'Settled' },
                ]} />
              <Tooltip label="Use your current location to show straight-line distance to each sales person's last spot">
                <Button onClick={askMyLocation}>{adminPos ? '✓ Distance from me' : 'Distance from me'}</Button>
              </Tooltip>
              {filtersActive && <Button variant="text" onClick={clearFilters}>Clear</Button>}
            </Space>
          }
        />
      </Card>
    </>
  );

  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 0 }}>Sales Person Advances</Typography.Title>
      <Typography.Paragraph type="secondary">
        Per-sales-person running cash wallet. Admin tops up the float; submitted expense reports auto-deduct it.
      </Typography.Paragraph>
      <Tabs
        items={[
          { key: 'advances', label: 'Sales person advances', children: advancesTab },
          {
            key: 'requests',
            label: (
              <Space size={6}>
                Pending requests
                {!!badges.advanceRequestsAwaitingMe && <Tag tone="red">{badges.advanceRequestsAwaitingMe}</Tag>}
              </Space>
            ),
            children: <AdvanceRequestsPage embedded />,
          },
        ]}
      />
      {drawerRepId && (
        <WalletDrawer repId={drawerRepId} canRecord={canRecord} canVerify={canRecord} onClose={() => setDrawerRepId(null)} />
      )}
      {giveFor && <GiveAdvanceModal rep={giveFor} onClose={() => setGiveFor(null)} />}
    </div>
  );
}

// Admin gives an advance straight into a sales person's wallet (posts ADVANCE_IN). The
// request → approve → disburse flow still lives on the 'Pending requests' tab for the
// approval-gated path (e.g. a Sales Head requesting on behalf).
function GiveAdvanceModal({ rep, onClose }: { rep: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | undefined>();
  const [mode, setMode] = useState('BANK_TRANSFER');
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => request({
      url: `/wallets/${rep.repId}/transactions`, method: 'POST',
      data: { type: 'ADVANCE_IN', amount: Number(amount), paymentMode: mode, paymentReference: ref || undefined, notes: note || undefined },
    }),
    onSuccess: () => {
      message.success('Advance given');
      qc.invalidateQueries({ queryKey: ['wallets'] });
      qc.invalidateQueries({ queryKey: ['wallet-stats'] });
      qc.invalidateQueries({ queryKey: ['wallet', String(rep.repId)] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!(Number(amount) > 0)} onClick={() => save.mutate()}>Give advance</Button></>}
      title={<span>Give advance <Tag tone="blue">{rep.repName ?? `#${rep.repId}`}</Tag></span>}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        Posts an <b>ADVANCE_IN</b> directly into the wallet (float). Current balance <b>{inr(rep.currentBalance)}</b>.
      </Typography.Paragraph>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <InputNumber min={1} placeholder="Amount ₹" className="w-full" value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        <Space wrap align="center">
          <span style={{ color: '#888' }}>Paid via</span>
          <Select value={mode} onChange={setMode} className="w-[150px]" options={['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'].map((m) => ({ value: m, label: m }))} />
          <Input placeholder="Ref / UTR" value={ref} onChange={(e) => setRef(e.target.value)} className="w-40" />
        </Space>
        <Textarea rows={2} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      </Space>
    </Modal>
  );
}

// ── Drawer: one rep's wallet header + ledger ─────────────────────────────────
function WalletDrawer({ repId, canRecord, canVerify, onClose }: { repId: string; canRecord: boolean; canVerify: boolean; onClose: () => void }) {
  const [recordOpen, setRecordOpen] = useState(false);
  const { data: wallet, isLoading } = useQuery({
    queryKey: ['wallet', repId],
    queryFn: () => request<any>({ url: `/wallets/${repId}` }),
  });

  return (
    <Drawer
      open
      onClose={onClose}
      width={760}
      title={<span>Wallet <Tag tone="blue" className="ml-1">{wallet?.repName ?? `#${repId}`}</Tag></span>}
      extra={canRecord ? (
        <Button variant="primary" icon={<Plus size={16} />} onClick={() => setRecordOpen(true)}>Record transaction</Button>
      ) : undefined}
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
      ) : !wallet ? (
        <EmptyState title="Wallet not found" />
      ) : (
        <WalletBody wallet={wallet} repId={repId} canVerify={canVerify} />
      )}
      {recordOpen && <RecordTxnModal repId={repId} onClose={() => setRecordOpen(false)} />}
    </Drawer>
  );
}

function WalletBody({ wallet, repId, canVerify, showLedger = true, showSummary = true }: { wallet: any; repId: string; canVerify: boolean; showLedger?: boolean; showSummary?: boolean }) {
  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#888' }}>Current balance</div>
        <div style={{ fontSize: 34, fontWeight: 800, color: balanceColor(wallet.currentBalance) }}>
          {inr(wallet.currentBalance)}
        </div>
        <Tag tone={balanceTone(wallet.currentBalance)}>
          {balanceLabel(wallet.currentBalance)}
        </Tag>
      </div>

      {showSummary && (
        <Descriptions bordered size="small" column={2} className="mb-5">
          <Descriptions.Item label="Total in">{inr(wallet.totalIn)}</Descriptions.Item>
          <Descriptions.Item label="Total out">{inr(wallet.totalOut)}</Descriptions.Item>
          <Descriptions.Item label="Last txn" span={2}>{formatIst(wallet.lastTxnAt)}</Descriptions.Item>
        </Descriptions>
      )}

      {showLedger && (
        <>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Ledger</Typography.Text>
          <LedgerTable repId={repId} transactions={wallet.transactions ?? []} canVerify={canVerify} />
        </>
      )}
    </>
  );
}

function LedgerTable({ repId, transactions, canVerify }: { repId: string; transactions: any[]; canVerify: boolean }) {
  const qc = useQueryClient();
  const [editRow, setEditRow] = useState<any>(null);
  const inval = () => {
    qc.invalidateQueries({ queryKey: ['wallet', repId] });
    qc.invalidateQueries({ queryKey: ['wallet-stats'] });
    qc.invalidateQueries({ queryKey: ['wallets'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const verify = useMutation({
    mutationFn: (txnId: string) => request({ url: `/wallets/${repId}/transactions/${txnId}/verify`, method: 'POST' }),
    onSuccess: () => { message.success('Transaction verified'); inval(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const del = useMutation({
    mutationFn: (txnId: string) => request({ url: `/wallets/${repId}/transactions/${txnId}`, method: 'DELETE' }),
    onSuccess: () => { message.success('Transaction reversed'); inval(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'txnNo', title: 'Txn No', dataIndex: 'txnNo', alwaysVisible: true },
    { key: 'txnDate', title: 'Date', dataIndex: 'txnDate', sortable: true, render: (v: string) => formatIstDate(v) },
    { key: 'type', title: 'Type', dataIndex: 'type', render: (v: string) => <Tag tone={TXN_TYPE_COLOR[v]}>{v}</Tag> },
    {
      key: 'direction', title: 'Dir', dataIndex: 'direction',
      render: (v: string) => <Tag tone={v === 'IN' ? 'green' : 'red'}>{v}</Tag>,
    },
    {
      key: 'amount', title: 'Amount', dataIndex: 'amount', align: 'right',
      render: (v: any, r: any) => <b style={{ color: r.direction === 'IN' ? '#1F8A4C' : '#D9363E' }}>{r.direction === 'IN' ? '+' : '−'}{inr(v)}</b>,
    },
    { key: 'balanceAfter', title: 'Balance after', dataIndex: 'balanceAfter', align: 'right', render: (v: any) => <span style={{ color: balanceColor(v) }}>{inr(v)}</span> },
    { key: 'paymentMode', title: 'Mode', dataIndex: 'paymentMode', defaultVisible: false, render: (v: any, r: any) => v ? `${v}${r.paymentReference ? ` · ${r.paymentReference}` : ''}` : '—' },
    {
      key: 'verificationStatus', title: 'Verify', dataIndex: 'verificationStatus', alwaysVisible: true, width: 150,
      render: (v: string, r: any) => (
        <Space size={6} wrap onClick={(e) => e.stopPropagation()}>
          <Tag tone={VERIFY_COLOR[v]}>{v === 'NOT_REQUIRED' ? 'N/A' : v}</Tag>
          {v === 'UNVERIFIED' && canVerify && !r.reversed && (
            <Popconfirm title="Mark this transaction verified?" okText="Verify" onConfirm={() => verify.mutate(r.id)}>
              <Button size="sm" variant="primary">Verify</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
    { key: 'attachmentUrl', title: 'Proof', dataIndex: 'attachmentUrl', defaultVisible: false, render: (v: any) => v ? <a href={v} target="_blank" rel="noreferrer">View</a> : '—' },
    { key: 'notes', title: 'Notes', dataIndex: 'notes', defaultVisible: false, ellipsis: true, render: (v: any, r: any) => v ?? r.reason ?? '—' },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 96,
      render: (_: any, r: any) => {
        if (r.reversed) return <Tag tone="neutral">Reversed</Tag>;
        // Only manually-posted, non-reversal rows can be edited/reversed (backend enforces too).
        const canManage = canVerify && r.referenceType === 'MANUAL' && !r.reversalOfTxnId;
        if (!canManage) return null;
        return (
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            <Tooltip label="Edit"><Button size="sm" aria-label="Edit" icon={<Pencil size={16} />} onClick={() => setEditRow(r)} /></Tooltip>
            <Popconfirm
              title="Reverse this transaction?"
              description="Posts an offsetting entry (net zero). The original stays for audit."
              okText="Reverse" okButtonProps={{ danger: true, loading: del.isPending }}
              onConfirm={() => del.mutate(r.id)}
            >
              <Tooltip label="Delete (reverse)"><Button size="sm" variant="danger" aria-label="Delete (reverse)" icon={<Trash2 size={16} />} /></Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        storageKey="wallet-ledger"
        rowKey="id"
        dataSource={transactions}
        columns={columns}
        pagination={{ pageSize: 15, showSizeChanger: true }}
        emptyText={<EmptyState title="No transactions yet" />}
      />
      {editRow && <EditTxnModal repId={repId} row={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); inval(); }} />}
    </>
  );
}

// ── Admin: edit a manually-posted ledger entry (recomputes the wallet) ────────
function EditTxnModal({ repId, row, onClose, onSaved }: { repId: string; row: any; onClose: () => void; onSaved: () => void }) {
  const [amount, setAmount] = useState<number | undefined>(Number(row.amount));
  const [direction, setDirection] = useState<'IN' | 'OUT'>(row.direction);
  const [txnDate, setTxnDate] = useState<Dayjs | null>(row.txnDate ? dayjs(row.txnDate) : null);
  const [paymentMode, setPaymentMode] = useState<string | undefined>(row.paymentMode ?? undefined);
  const [paymentReference, setPaymentReference] = useState(row.paymentReference ?? '');
  const [notes, setNotes] = useState(row.notes ?? '');
  const isAdjustment = row.type === 'ADJUSTMENT' || row.type === 'OPENING_BALANCE';

  const save = useMutation({
    mutationFn: () => request({
      url: `/wallets/${repId}/transactions/${row.id}`, method: 'PATCH',
      data: {
        amount, txnDate: txnDate ? txnDate.format('YYYY-MM-DD') : undefined,
        paymentMode: paymentMode || undefined, paymentReference: paymentReference || undefined,
        notes: notes || undefined, ...(isAdjustment ? { direction } : {}),
      },
    }),
    onSuccess: () => { message.success('Transaction updated'); onSaved(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to update'),
  });

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Edit ${row.txnNo}`}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!(Number(amount) > 0)} onClick={() => save.mutate()}>Save</Button></>}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Tag tone={TXN_TYPE_COLOR[row.type]}>{row.type}</Tag>
        <div><div style={{ fontSize: 12, marginBottom: 4 }}>Amount ₹ <span style={{ color: '#D9363E' }}>*</span></div>
          <InputNumber min={1} className="w-full" value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} /></div>
        {isAdjustment && (
          <div><div style={{ fontSize: 12, marginBottom: 4 }}>Direction</div>
            <Select className="w-full" value={direction} onChange={(v) => setDirection(v as 'IN' | 'OUT')}
              options={[{ value: 'IN', label: 'IN' }, { value: 'OUT', label: 'OUT' }]} /></div>
        )}
        <div><div style={{ fontSize: 12, marginBottom: 4 }}>Date</div>
          <DatePicker className="w-full" value={txnDate} onChange={setTxnDate} /></div>
        <div><div style={{ fontSize: 12, marginBottom: 4 }}>Payment mode</div>
          <Input value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} placeholder="CASH / UPI / BANK_TRANSFER…" /></div>
        <div><div style={{ fontSize: 12, marginBottom: 4 }}>Reference</div>
          <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} /></div>
        <div><div style={{ fontSize: 12, marginBottom: 4 }}>Notes</div>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Editing the amount recomputes this wallet's running balances.</Typography.Text>
      </Space>
    </Modal>
  );
}

// ── Admin: record a wallet transaction ───────────────────────────────────────
function RecordTxnModal({ repId, onClose }: { repId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState<typeof TXN_TYPES[number]>('ADVANCE_IN');
  const [amount, setAmount] = useState<number | undefined>();
  const [direction, setDirection] = useState<'IN' | 'OUT'>('IN');
  const [paymentMode, setPaymentMode] = useState<string | undefined>();
  const [paymentReference, setPaymentReference] = useState('');
  const [txnDate, setTxnDate] = useState<Dayjs | null>(dayjs());
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState<string | undefined>();

  const needsDirection = type === 'ADJUSTMENT' || type === 'OPENING_BALANCE';
  const needsReason = type === 'ADJUSTMENT';

  const save = useMutation({
    mutationFn: () => request({
      url: `/wallets/${repId}/transactions`, method: 'POST',
      data: {
        type,
        amount: Number(amount),
        direction: needsDirection ? direction : undefined,
        paymentMode: paymentMode || undefined,
        paymentReference: paymentReference.trim() || undefined,
        txnDate: txnDate ? txnDate.format('YYYY-MM-DD') : undefined,
        reason: needsReason ? reason.trim() : undefined,
        notes: notes.trim() || undefined,
        attachmentUrl: attachmentUrl || undefined,
      },
    }),
    onSuccess: () => {
      message.success('Transaction recorded');
      qc.invalidateQueries({ queryKey: ['wallet', repId] });
      qc.invalidateQueries({ queryKey: ['wallet-stats'] });
      qc.invalidateQueries({ queryKey: ['wallets'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const disabled = !(Number(amount) > 0) || (needsReason && !reason.trim());

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Record transaction"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={disabled} onClick={() => save.mutate()}>Record</Button></>}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Type</div>
          <Select className="w-full" value={type} onChange={(v) => setType(v as typeof TXN_TYPES[number])}
            options={TXN_TYPES.map((t) => ({ value: t, label: t }))} />
        </div>
        {needsDirection && (
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Direction</div>
            <Select className="w-full" value={direction} onChange={(v) => setDirection(v as 'IN' | 'OUT')}
              options={[{ value: 'IN', label: 'IN (credit to rep)' }, { value: 'OUT', label: 'OUT (debit from rep)' }]} />
          </div>
        )}
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Amount</div>
          <InputNumber min={1} placeholder="Amount ₹" className="w-full" value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Payment mode</div>
          <Select className="w-full" placeholder="Payment mode (optional)" value={paymentMode} onChange={setPaymentMode}
            options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))} />
        </div>
        <Input placeholder="Payment reference / UTR (optional)" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Txn date</div>
          <DatePicker className="w-full" value={txnDate} onChange={setTxnDate} />
        </div>
        {needsReason && (
          <Textarea rows={2} placeholder="Reason (required for adjustment)" value={reason} onChange={(e) => setReason(e.target.value)} />
        )}
        <Textarea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <FileUploadField category="receipt" buttonLabel="Upload proof (optional)" value={attachmentUrl} onChange={setAttachmentUrl} />
      </Space>
    </Modal>
  );
}

// ── Rep view: their own wallet ───────────────────────────────────────────────
function MyWalletView() {
  const [topUpOpen, setTopUpOpen] = useState(false);
  const myId = useAuthStore((s) => s.user?.id);
  const badges = useBadges();
  const canRequest = usePermission('ADVANCE.REQUEST'); // backend requires it; don't show a button that 403s
  const { data: wallet, isLoading } = useQuery({
    queryKey: ['wallet', 'me'],
    queryFn: () => request<any>({ url: '/wallets/me' }),
  });

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} align="center" wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>My wallet</Typography.Title>
        <Space wrap>
          {canRequest && <Button variant="primary" icon={<Plus size={16} />} onClick={() => setTopUpOpen(true)}>Request top-up</Button>}
        </Space>
      </Space>

      <Card>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
        ) : !wallet ? (
          <EmptyState title="No wallet found" />
        ) : (
          <WalletBody wallet={wallet} repId={String(wallet.repId)} canVerify={false} showLedger={false} showSummary={false} />
        )}
      </Card>

      {myId && (
        <Card className="mt-4">
          <Tabs
            items={[
              { key: 'expenses', label: 'My expenses', children: <MyExpensesTab repId={String(myId)} /> },
              {
                key: 'topups',
                label: (
                  <Space size={6}>
                    Top-up activity
                    {!!badges.myAdvanceRequestsPending && (
                      <Tag tone="gold">{badges.myAdvanceRequestsPending}</Tag>
                    )}
                  </Space>
                ),
                children: <MyTopUpsTab />,
              },
            ]}
          />
        </Card>
      )}

      {topUpOpen && <TopUpModal onClose={() => setTopUpOpen(false)} />}
    </div>
  );
}

// Top-up (advance) activity — the logged-in sales person's own requests across every status,
// so they can track REQUESTED → APPROVED → DISBURSED (or REJECTED) with full detail.
function MyTopUpsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['advance-requests', 'mine'],
    queryFn: () => request<any[]>({ url: '/advance-requests' }),
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'requestNo', title: 'Request No', dataIndex: 'requestNo', alwaysVisible: true, sortable: true },
    { key: 'amount', title: 'Amount', dataIndex: 'amount', align: 'right', sortable: true, render: (v: any) => <b>{inr(v)}</b> },
    { key: 'purpose', title: 'Purpose', dataIndex: 'purpose', ellipsis: true, render: (v: any) => v || '—' },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, render: (s: string) => <Tag tone={ADV_STATUS_COLOR[s] ?? 'neutral'}>{s}</Tag> },
    { key: 'createdAt', title: 'Requested', dataIndex: 'createdAt', sortable: true, render: (v: string) => formatIstDate(v) },
    { key: 'approvedByName', title: 'Approved by', dataIndex: 'approvedByName', render: (v: any, r: any) => (v ? <Space size={4} wrap>{v}<RoleTag role={r.approvedByRole} />{r.approvedAt && <span style={{ color: '#888', fontSize: 12 }}>{formatIstDate(r.approvedAt)}</span>}</Space> : '—') },
    {
      key: 'disbursed', title: 'Disbursed', dataIndex: 'disbursedAt',
      render: (_: any, r: any) => r.status === 'DISBURSED'
        ? <Space size={4} wrap><span style={{ color: '#1F8A4C' }}>{formatIstDate(r.disbursedAt)}</span>{r.paymentMode && <Tag>{r.paymentMode}{r.paymentReference ? ` · ${r.paymentReference}` : ''}</Tag>}</Space>
        : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      key: 'paymentProofUrl', title: 'Proof', dataIndex: 'paymentProofUrl', align: 'center',
      render: (v: any) => v ? <a href={v} target="_blank" rel="noreferrer">View</a> : <span style={{ color: '#ccc' }}>—</span>,
    },
    { key: 'rejectReason', title: 'Note', dataIndex: 'rejectReason', ellipsis: true, render: (v: any, r: any) => r.status === 'REJECTED' ? <span style={{ color: '#D9363E' }}>{v || 'Rejected'}</span> : '—' },
  ];

  return (
    <DataTable
      storageKey="my-topups"
      rowKey="id"
      loading={isLoading}
      dataSource={data ?? []}
      columns={columns}
      pagination={{ pageSize: 10, showSizeChanger: true }}
      expandable={{ expandedRowRender: (r: any) => <RequestDetailPanel req={r} />, expandRowByClick: true }}
      emptyText={<EmptyState title="No top-up requests yet" />}
    />
  );
}

// My expenses — every expense report filed by / for the logged-in user. Clicking a row opens
// a modal with that report's full wallet ledger trail (EXPENSE_OUT / reversal / adjustment).
function MyExpensesTab({ repId }: { repId: string }) {
  const [openReport, setOpenReport] = useState<any | null>(null);
  const { data: reports, isLoading } = useQuery({
    queryKey: ['my-expense-reports', repId],
    queryFn: () => request<any[]>({ url: '/expense-reports', params: { repId } }),
  });
  // All wallet rows tied to an expense report, fetched once and grouped by report id.
  const { data: ledger } = useQuery({
    queryKey: ['my-expense-ledger', repId],
    queryFn: () => request<any[]>({ url: `/wallets/${repId}/transactions`, params: { referenceType: 'EXPENSE_REPORT' } }),
  });
  const ledgerByReport = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const t of ledger ?? []) {
      const k = String(t.referenceId);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return m;
  }, [ledger]);

  const columns: DataTableColumn<any>[] = [
    { key: 'reportNo', title: 'Report No', dataIndex: 'reportNo', alwaysVisible: true, sortable: true },
    {
      key: 'partyName', title: 'Party / Visit', dataIndex: 'partyName',
      render: (v: any, r: any) => (
        <Space size={4} wrap>{v ?? <span style={{ color: '#ccc' }}>—</span>}{r.visitType && <Tag>{String(r.visitType).replace(/_/g, ' ')}</Tag>}</Space>
      ),
    },
    { key: 'subtotalAmount', title: 'Amount', dataIndex: 'subtotalAmount', align: 'right', sortable: true, render: (v: any) => <b>{inr(v)}</b> },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, render: (s: string) => <Tag tone={REPORT_STATUS_COLOR[s] ?? 'neutral'}>{(s ?? '').replace(/_/g, ' ')}</Tag> },
    { key: 'filed', title: 'Filed', dataIndex: 'createdAt', sortable: true, render: (v: string, r: any) => formatIstDate(r.submittedAt ?? v) },
    {
      key: 'ledger', title: 'Ledger', align: 'center', width: 90,
      render: (_: any, r: any) => {
        const n = ledgerByReport.get(String(r.id))?.length ?? 0;
        return n ? <Tag tone="blue">{n} txn</Tag> : <span style={{ color: '#ccc' }}>—</span>;
      },
    },
  ];

  return (
    <>
      <DataTable
        storageKey="my-expenses"
        rowKey="id"
        loading={isLoading}
        dataSource={reports ?? []}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        onRow={(r: any) => ({ onClick: () => setOpenReport(r), style: { cursor: 'pointer' } })}
        emptyText={<EmptyState title="No expenses yet" />}
      />
      {openReport && (
        <ExpenseLedgerModal
          report={openReport}
          ledger={ledgerByReport.get(String(openReport.id)) ?? []}
          onClose={() => setOpenReport(null)}
        />
      )}
    </>
  );
}

// One expense's money trail — the report summary + every wallet ledger row linked to it.
function ExpenseLedgerModal({ report, ledger, onClose }: { report: any; ledger: any[]; onClose: () => void }) {
  const navigate = useNavigate();
  const columns = [
    { title: 'Txn No', dataIndex: 'txnNo' },
    { title: 'Date', dataIndex: 'txnDate', render: (v: string) => formatIstDate(v) },
    { title: 'Type', dataIndex: 'type', render: (v: string) => <Tag tone={TXN_TYPE_COLOR[v]}>{v}</Tag> },
    { title: 'Dir', dataIndex: 'direction', render: (v: string) => <Tag tone={v === 'IN' ? 'green' : 'red'}>{v}</Tag> },
    {
      title: 'Amount', dataIndex: 'amount', align: 'right' as const,
      render: (v: any, r: any) => <b style={{ color: r.direction === 'IN' ? '#1F8A4C' : '#D9363E' }}>{r.direction === 'IN' ? '+' : '−'}{inr(v)}</b>,
    },
    { title: 'Balance after', dataIndex: 'balanceAfter', align: 'right' as const, render: (v: any) => <span style={{ color: balanceColor(v) }}>{inr(v)}</span> },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: (v: any, r: any) => v ?? r.reason ?? '—' },
  ];

  return (
    <Modal
      open width={820} onOpenChange={(o) => { if (!o) onClose(); }}
      title={<Space wrap>Expense {report.reportNo}<Tag tone={REPORT_STATUS_COLOR[report.status] ?? 'neutral'}>{(report.status ?? '').replace(/_/g, ' ')}</Tag></Space>}
      footer={[
        <Button key="close" variant="default" onClick={onClose}>Close</Button>,
        <Button key="open" variant="primary" onClick={() => navigate(`/expense-reports/${report.id}`)}>Open full report →</Button>,
      ]}
    >
      <Descriptions bordered size="small" column={2} className="mb-4">
        <Descriptions.Item label="Party">{report.partyName ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Visit type">{report.visitType ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Amount">{inr2(report.subtotalAmount)}</Descriptions.Item>
        <Descriptions.Item label="Filed">{formatIst(report.createdAt)}</Descriptions.Item>
      </Descriptions>

      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Transaction ledger</Typography.Text>
      <Table
        rowKey="id" size="small" pagination={false} dataSource={ledger} columns={columns as any}
        locale={{ emptyText: <EmptyState title="No wallet transactions yet — not submitted." /> }}
      />
    </Modal>
  );
}

function TopUpModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | undefined>();
  const [purpose, setPurpose] = useState('');

  const save = useMutation({
    mutationFn: () => request({ url: '/advance-requests/request', method: 'POST', data: { amount: Number(amount), purpose: purpose.trim() } }),
    onSuccess: () => {
      message.success('Top-up requested');
      qc.invalidateQueries({ queryKey: ['advance-requests'] });
      qc.invalidateQueries({ queryKey: ['badges'] }); // light up the pending badge immediately
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Request top-up"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!(Number(amount) > 0) || !purpose.trim()} onClick={() => save.mutate()}>Request</Button></>}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <InputNumber min={1} placeholder="Amount ₹" className="w-full" value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        <Textarea rows={2} placeholder="Purpose (e.g. 3-day Pune-east tour)" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
      </Space>
    </Modal>
  );
}

// Compact, colour-coded KPI tile (greyed out when zero so live numbers stand out).
function KpiTile({ label, value, color, bg, muted }: { label: string; value: string; color: string; bg: string; muted?: boolean }) {
  return (
    <div style={{
      flex: '1 1 0', minWidth: 150, borderRadius: 10, padding: '12px 16px',
      background: muted ? '#fafafa' : bg, borderLeft: `4px solid ${muted ? '#e5e5e5' : color}`,
    }}>
      <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: muted ? '#bbb' : color }}>{value}</div>
    </div>
  );
}
